import { h, type FunctionalComponent } from "vue";

type IconProps = {
  class?: string;
};

function icon(
  paths: string[],
  extras: Record<string, unknown> = {},
): FunctionalComponent<IconProps> {
  return (props) =>
    h(
      "svg",
      {
        xmlns: "http://www.w3.org/2000/svg",
        viewBox: "0 0 24 24",
        fill: "none",
        stroke: "currentColor",
        "stroke-width": "1.75",
        "stroke-linecap": "round",
        "stroke-linejoin": "round",
        class: props.class,
        ...extras,
      },
      paths.map((d) => h("path", { d })),
    );
}

function svgIcon(
  children: ReturnType<typeof h>[],
): FunctionalComponent<IconProps> {
  return (props) =>
    h(
      "svg",
      {
        xmlns: "http://www.w3.org/2000/svg",
        viewBox: "0 0 24 24",
        fill: "none",
        stroke: "currentColor",
        "stroke-width": "1.75",
        "stroke-linecap": "round",
        "stroke-linejoin": "round",
        class: props.class,
      },
      children,
    );
}

export const IconUndo = icon([
  "M9 14 4 9l5-5",
  "M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5v0a5.5 5.5 0 0 1-5.5 5.5H11",
]);

export const IconRedo = icon([
  "M15 14l5-5-5-5",
  "M20 9H9.5A5.5 5.5 0 0 0 4 14.5v0A5.5 5.5 0 0 0 9.5 20H13",
]);

export const IconBold = icon([
  "M6 4h8a4 4 0 0 1 0 8H6z",
  "M6 12h9a4 4 0 0 1 0 8H6z",
]);

export const IconItalic = icon(["M19 4h-9", "M14 20H5", "M15 4 9 20"]);

export const IconUnderline = icon(["M6 4v7a6 6 0 0 0 12 0V4", "M4 20h16"]);

export const IconStrikethrough = icon([
  "M16 4H9a3 3 0 0 0 0 6h6a3 3 0 0 1 0 6H8",
  "M4 12h16",
]);

export const IconHighlight: FunctionalComponent<IconProps> = (props) =>
  h(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      "stroke-width": "1.75",
      "stroke-linecap": "round",
      "stroke-linejoin": "round",
      class: props.class,
    },
    [
      h("path", { d: "m9 11-6 6v3h3l6-6" }),
      h("path", {
        d: "m22 12-4.6 4.6a2 2 0 0 1-2.8 0l-5.2-5.2a2 2 0 0 1 0-2.8L14 4",
      }),
    ],
  );

export const IconBulletList = icon([
  "M8 6h13",
  "M8 12h13",
  "M8 18h13",
  "M3 6h.01",
  "M3 12h.01",
  "M3 18h.01",
]);

export const IconNumberedList: FunctionalComponent<IconProps> = (props) =>
  h(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      "stroke-width": "1.75",
      "stroke-linecap": "round",
      "stroke-linejoin": "round",
      class: props.class,
    },
    [
      h("path", { d: "M10 6h11" }),
      h("path", { d: "M10 12h11" }),
      h("path", { d: "M10 18h11" }),
      h("path", { d: "M4 6h1v4" }),
      h("path", { d: "M4 10h2" }),
      h("path", { d: "M6 18H4c0-1 2-2 2-3s-1-1.5-2-1" }),
    ],
  );

export const IconQuote = icon(["M3 6v12", "M8 6h13", "M8 12h10", "M8 18h8"]);

/** Decrease indent: arrow points left (away from text). */
export const IconOutdent = icon([
  "M21 6H11",
  "M21 12H9",
  "M21 18H11",
  "M7 8l-4 4 4 4",
]);

/** Increase indent: arrow points right (into text). */
export const IconIndent = icon([
  "M21 6H11",
  "M21 12H9",
  "M21 18H11",
  "M3 8l4 4-4 4",
]);

export const IconAlignLeft = icon(["M21 6H3", "M15 12H3", "M17 18H3"]);

export const IconAlignCenter = icon(["M21 6H3", "M17 12H7", "M19 18H5"]);

export const IconAlignRight = icon(["M21 6H3", "M21 12H9", "M21 18H7"]);

export const IconLink = icon([
  "M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71",
  "M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71",
]);

export const IconImage: FunctionalComponent<IconProps> = (props) =>
  h(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      "stroke-width": "1.75",
      "stroke-linecap": "round",
      "stroke-linejoin": "round",
      class: props.class,
    },
    [
      h("rect", { width: "18", height: "18", x: "3", y: "3", rx: "2" }),
      h("circle", { cx: "9", cy: "9", r: "2" }),
      h("path", { d: "m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" }),
    ],
  );

export const IconSpacer = icon(["M4 8h16", "M4 16h16", "M12 8v8"]);

export const IconComment = icon(["M7.9 20A9 9 0 1 0 4 16.1L2 22z"]);

/** Outline: writing / manuscript */
export const IconWriting = icon([
  "M12.67 19a2 2 0 0 0 1.416-.588l6.154-6.172a6 6 0 0 0-8.49-8.49L5.586 9.914A2 2 0 0 0 5 11.328V18a1 1 0 0 0 1 1z",
  "M16 8 2 22",
  "M17.5 15H9",
]);

/** Outline: timeline */
export const IconTimeline = svgIcon([
  h("path", { d: "M6 3v1m0 4v2m0 4v2m0 4v1M12 6h8M12 12h6M12 18h8" }),
  h("circle", { cx: "6", cy: "6", r: "2" }),
  h("circle", { cx: "6", cy: "12", r: "2" }),
  h("circle", { cx: "6", cy: "18", r: "2" }),
]);

/** Drag handle — a dedicated grab target for the Storyboard card/band grip,
 * distinct from the title/synopsis text so drag never fights inline
 * editing (filled dots, not a stroked icon like the rest of this file). */
export const IconGripVertical: FunctionalComponent<IconProps> = (props) =>
  h(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 24 24",
      fill: "currentColor",
      class: props.class,
    },
    [
      h("circle", { cx: "9", cy: "6", r: "1.4" }),
      h("circle", { cx: "9", cy: "12", r: "1.4" }),
      h("circle", { cx: "9", cy: "18", r: "1.4" }),
      h("circle", { cx: "15", cy: "6", r: "1.4" }),
      h("circle", { cx: "15", cy: "12", r: "1.4" }),
      h("circle", { cx: "15", cy: "18", r: "1.4" }),
    ],
  );

/** Outline: global search */
export const IconSearch = icon([
  "m21 21-4.3-4.3",
  "M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14Z",
]);

/** Outline: scratch notes */
export const IconNotes = icon([
  "M15.5 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8.5L15.5 3Z",
  "M15 3v5h5",
  "M8 13h8",
  "M8 17h5",
]);

/** Outline: worldbuilding (characters, places, lore) — Lucide globe */
export const IconGlobe: FunctionalComponent<IconProps> = (props) =>
  h(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      "stroke-width": "2",
      "stroke-linecap": "round",
      "stroke-linejoin": "round",
      class: props.class,
    },
    [
      h("path", {
        d: "M21.54 15H17a2 2 0 0 0-2 2v4.54",
      }),
      h("path", {
        d: "M7 3.34V5a3 3 0 0 0 3 3a2 2 0 0 1 2 2c0 1.1.9 2 2 2a2 2 0 0 0 2-2c0-1.1.9-2 2-2h3.17",
      }),
      h("path", {
        d: "M11 21.95V18a2 2 0 0 0-2-2a2 2 0 0 1-2-2v-1a2 2 0 0 0-2-2H2.05",
      }),
      h("circle", { cx: "12", cy: "12", r: "10" }),
    ],
  );

/** Outline: filter (funnel) */
export const IconFilter = icon(["M22 3H2l8 9.46V19l4 2v-8.54L22 3z"]);

/** Outline: trash / restore deleted files */
export const IconTrash = icon([
  "M3 6h18",
  "M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6",
  "M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2",
  "M10 11v6",
  "M14 11v6",
]);

function headingIcon(label: string): FunctionalComponent<IconProps> {
  return (props) =>
    h(
      "svg",
      {
        xmlns: "http://www.w3.org/2000/svg",
        viewBox: "0 0 24 24",
        fill: "none",
        stroke: "currentColor",
        "stroke-width": "1.75",
        "stroke-linecap": "round",
        "stroke-linejoin": "round",
        class: props.class,
      },
      [
        h("path", { d: "M4 12h8" }),
        h("path", { d: "M4 18V6" }),
        h("path", { d: "M12 18V6" }),
        h(
          "text",
          {
            x: "16.5",
            y: "18",
            fill: "currentColor",
            stroke: "none",
            "font-size": "9",
            "font-family": "ui-sans-serif, system-ui, sans-serif",
            "font-weight": "600",
          },
          label,
        ),
      ],
    );
}

export const IconHeading1 = headingIcon("1");
export const IconHeading2 = headingIcon("2");
export const IconHeading3 = headingIcon("3");

/** Collapse / minimize a panel back to a compact preview. */
export const IconCollapse = icon([
  "M8 3v3a2 2 0 0 1-2 2H3",
  "M21 8h-3a2 2 0 0 1-2-2V3",
  "M3 16h3a2 2 0 0 1 2 2v3",
  "M16 21v-3a2 2 0 0 1 2-2h3",
]);

/** Map: move / select (Photoshop-style pointer). */
export const IconMapSelect = icon(["m3 3 7.07 16.97 2.51-7.39 7.39-2.51L3 3z"]);

/** Map: point-to-point line. */
export const IconMapLine = svgIcon([
  h("path", { d: "M6 18 18 6" }),
  h("circle", { cx: "6", cy: "18", r: "2.25" }),
  h("circle", { cx: "18", cy: "6", r: "2.25" }),
]);

/** Map: fill a closed region. */
export const IconMapFill = svgIcon([
  h("path", {
    d: "m19 11-8-8-8.6 8.6a2 2 0 0 0 0 2.8l5.2 5.2c.8.8 2 .8 2.8 0L19 11Z",
  }),
  h("path", { d: "m5 2 5 5" }),
  h("path", { d: "M2 13h15" }),
  h("path", {
    d: "M22 20a2 2 0 1 1-4 0c0-1.6 1.7-2.4 2-4 .3 1.6 2 2.4 2 4",
  }),
]);

/** Map: sculpt land vs sea. */
export const IconMapLandmass = svgIcon([
  h("path", {
    d: "M4 18c2-4 3-7 5-7s2 3 4 3 2-4 4-4 2 3 3 5",
  }),
  h("path", { d: "M3 20h18" }),
  h("circle", { cx: "9", cy: "8", r: "2.25" }),
]);

/** Map: carve lakes and rivers into the landmass. */
export const IconMapWater = svgIcon([
  h("path", {
    d: "M12 3c-2.8 4.2-6 7.4-6 11a6 6 0 0 0 12 0c0-3.6-3.2-6.8-6-11Z",
  }),
  h("path", { d: "M9.5 14.5c.6 1.2 1.5 1.8 2.5 1.8" }),
]);

/** Map: paint terrain tint on existing land (does not grow the mask). */
export const IconMapTerrain = svgIcon([
  h("path", {
    d: "m14.5 4.5 5 5L9 20H4v-5z",
  }),
  h("path", { d: "m13 6 5 5" }),
  h("path", {
    d: "M4 18c1.5-2.5 2.5-4 4-4s1.5 1.5 3 1.5 1.5-2.5 3-2.5",
  }),
]);

/** Map: place landmark. */
export const IconMapPin = svgIcon([
  h("path", {
    d: "M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z",
  }),
  h("circle", { cx: "12", cy: "10", r: "2.5" }),
]);

/** Map: story path route (stub tool). */
export const IconMapStoryPath = svgIcon([
  h("path", { d: "M5 18c3-2 5-8 7-8s3 5 7 5" }),
  h("circle", { cx: "5", cy: "18", r: "2" }),
  h("circle", { cx: "12", cy: "10", r: "2" }),
  h("circle", { cx: "19", cy: "15", r: "2" }),
]);

/** Map: eraser. */
export const IconMapEraser = icon([
  "m7 21-4.3-4.3c-1-1-1-2.5 0-3.4l9.6-9.6c1-1 2.5-1 3.4 0l5.6 5.6c1 1 1 2.5 0 3.4L13 21",
  "M22 21H7",
  "m5 11 9 9",
]);

/** Map: type tool. */
export const IconMapType = icon(["M6 4h12", "M12 4v16", "M8 20h8"]);

/** Map: layer stack. */
export const IconMapLayers = icon([
  "M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83z",
  "M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 12",
  "M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 17",
]);

/** Visibility on (layer eye). */
export const IconEye = svgIcon([
  h("path", {
    d: "M2.06 12.35c.2.4 2.9 5.65 9.94 5.65s9.74-5.25 9.94-5.65a.5.5 0 0 0 0-.7C21.74 10.9 19.04 5.65 12 5.65S2.26 10.9 2.06 11.65a.5.5 0 0 0 0 .7Z",
  }),
  h("circle", { cx: "12", cy: "12", r: "3" }),
]);

/** Visibility off. */
export const IconEyeOff = svgIcon([
  h("path", {
    d: "M10.7 5.85A10.5 10.5 0 0 1 12 5.65c6.04 0 8.74 5.25 8.94 5.65a.5.5 0 0 1 0 .7c-.12.24-1.1 2.05-2.94 3.55",
  }),
  h("path", {
    d: "M6.6 6.6C4.08 8.2 2.5 10.7 2.06 11.65a.5.5 0 0 0 0 .7c.2.4 2.9 5.65 9.94 5.65 1.1 0 2.1-.15 3-.4",
  }),
  h("path", { d: "m3 3 18 18" }),
  h("path", { d: "M9.9 9.9a3 3 0 0 0 4.2 4.2" }),
]);

/** Layer locked. */
export const IconLock = svgIcon([
  h("rect", { x: "5", y: "11", width: "14", height: "10", rx: "2" }),
  h("path", { d: "M8 11V7a4 4 0 0 1 8 0v4" }),
]);

/** Layer unlocked. */
export const IconUnlock = svgIcon([
  h("rect", { x: "5", y: "11", width: "14", height: "10", rx: "2" }),
  h("path", { d: "M8 11V7a4 4 0 0 1 7.3-2.3" }),
]);

/** Map: export image. */
export const IconMapExport = icon([
  "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4",
  "m7 10 5 5 5-5",
  "M12 15V3",
]);

/** Gear / settings. */
export const IconSettings = svgIcon([
  h("path", {
    d: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 0 0 1.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 0 0-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 0 0-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 0 0-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 0 0-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 0 0 1.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.573-1.066Z",
  }),
  h("path", { d: "M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" }),
]);

/** Rotate 90° clockwise. */
export const IconRotateCw = icon(["M21 12a9 9 0 1 1-2.64-6.36", "M21 3v6h-6"]);

/** Rotate 90° counterclockwise. */
export const IconRotateCcw = icon(["M3 12a9 9 0 1 0 2.64-6.36", "M3 3v6h6"]);

/** Single page placement. */
export const IconPageSingle = svgIcon([
  h("rect", { x: "6", y: "3", width: "12", height: "18", rx: "1.5" }),
]);

/** Two-page spread: one figure across facing pages. */
export const IconPageSpread = svgIcon([
  h("rect", { x: "2", y: "4", width: "9", height: "16", rx: "1" }),
  h("rect", { x: "13", y: "4", width: "9", height: "16", rx: "1" }),
]);

/** Print extent: fit inside text margins. */
export const IconExtentText = svgIcon([
  h("rect", { x: "4", y: "2", width: "16", height: "20", rx: "1.5" }),
  h("rect", { x: "8", y: "6", width: "8", height: "12", rx: "0.5" }),
]);

/** Print extent: printer safe zone (~0.5in from trim). */
export const IconExtentSafe = svgIcon([
  h("rect", { x: "4", y: "2", width: "16", height: "20", rx: "1.5" }),
  h("rect", { x: "6.5", y: "4.5", width: "11", height: "15", rx: "0.5" }),
]);

/** Print extent: full bleed to trim edge. */
export const IconExtentBleed = svgIcon([
  h("rect", {
    x: "4",
    y: "2",
    width: "16",
    height: "20",
    rx: "1.5",
    fill: "currentColor",
    "fill-opacity": "0.22",
  }),
  h("rect", { x: "4", y: "2", width: "16", height: "20", rx: "1.5" }),
]);

/** Map object-fit: contain (letterbox inside the slot). */
export const IconFitContain = svgIcon([
  h("rect", { x: "4", y: "2", width: "16", height: "20", rx: "1.5" }),
  h("rect", {
    x: "7",
    y: "7",
    width: "10",
    height: "10",
    rx: "0.5",
    fill: "currentColor",
    "fill-opacity": "0.35",
  }),
]);

/** Map object-fit: cover (fill slot, may crop). */
export const IconFitCover = svgIcon([
  h("rect", {
    x: "4",
    y: "2",
    width: "16",
    height: "20",
    rx: "1.5",
    fill: "currentColor",
    "fill-opacity": "0.35",
  }),
  h("rect", { x: "4", y: "2", width: "16", height: "20", rx: "1.5" }),
]);

/** Open map in the Maps editor. */
export const IconOpenMap = icon([
  "M3 6.5 9 4l6 2.5L21 4v13.5L15 20l-6-2.5L3 20z",
  "M9 4v13.5",
  "M15 6.5V20",
]);

/** Edit (pencil). */
export const IconEdit = icon([
  "M12 20h9",
  "M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z",
]);

/** Edit map: quieter map + full-weight pencil so “edit” is the read. */
export const IconEditMap = svgIcon([
  h("g", { opacity: "0.55" }, [
    h("path", {
      d: "M2 6.5 7.5 4.2l5 2.1L17.5 4.2v11.5L12.5 17.8l-5-2.1L2 17.8z",
    }),
    h("path", { d: "M7.5 4.2v11.5" }),
    h("path", { d: "M12.5 6.3v11.5" }),
  ]),
  h("path", { d: "M13 21h8" }),
  h("path", {
    d: "M17.2 12.2a2 2 0 0 1 2.8 2.8L14.5 20.5 11 21.5l1-3.5z",
  }),
]);

/** Map: keyboard shortcuts. */
export const IconKeyboard = svgIcon([
  h("rect", { x: "2", y: "6", width: "20", height: "12", rx: "2" }),
  h("path", { d: "M6 10h.01M10 10h.01M14 10h.01M18 10h.01M8 13h8" }),
]);

export const IconMapCrop = icon(["M6 3v15h15", "M3 6h15v15"]);
